<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import {
  NCard,
  NButton,
  NInput,
  NSpace,
  NEmpty,
  NAlert,
  NSpin,
  NModal,
  NUpload,
  NIcon,
  NProgress,
  NGrid,
  NGridItem,
  NTag,
  NTooltip,
  useMessage
} from 'naive-ui';
import {
  Upload,
  Search,
  File,
  FileText,
  FileImage,
  Trash2,
  Eye,
  Lock,
  FolderOpen,
  AlertCircle,
  CheckCircle
} from 'lucide-vue-next';

import { authToken } from '@/services/auth';
import * as documentService from '@/services/patientDocument';
import { decryptPatientData, encryptPatientData } from '@/services/encryption';

const router = useRouter();
const message = useMessage();

// Component state
const isLoading = ref(false);
const documents = ref([]);
const slotsUsed = ref(0);
const slotsAvailable = ref(1);

// Password state - MATCH PatientProfile.vue naming
const decryptionPassword = ref('');
const showPasswordModal = ref(true);
const isDecrypting = ref(false);
const passwordError = ref('');

// Search state
const searchQuery = ref('');

// Upload state
const showUploadModal = ref(false);
const uploadFile = ref(null);
const uploadFileName = ref('');
const uploadNotes = ref('');
const isUploading = ref(false);
const uploadError = ref('');

// Delete state
const documentToDelete = ref(null);
const showDeleteModal = ref(false);
const isDeleting = ref(false);

// Decrypted documents cache
const decryptedDocs = ref([]);

// Get file icon helper
const getFileIcon = (fileType, fileName) => {
  const icon = documentService.getFileIcon(fileType, fileName);
  const iconMap = {
    'FileImage': FileImage,
    'FileText': FileText,
    'File': File
  };
  return iconMap[icon] || File;
};

// Format file size helper
const formatSize = (bytes) => {
  return documentService.formatFileSize(bytes);
};

// Format date helper
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Decrypt all documents - MATCH PatientProfile.vue logic EXACTLY
const decryptData = async () => {
  if (!decryptionPassword.value.trim()) {
    passwordError.value = 'Please enter your patient password';
    return false;
  }

  try {
    isDecrypting.value = true;
    passwordError.value = '';

    const decrypted = [];
    
    if (!documents.value || documents.value.length === 0) {
      showPasswordModal.value = false;
      return true;
    }

    for (const doc of documents.value) {
      try {
        console.log('Decrypting document:', doc.id);

        // Use password WITHOUT trim - exactly like PatientProfile
        const fileName = await decryptPatientData(
          doc.encrypted_file_name,
          doc.encryption_metadata,
          decryptionPassword.value  // ← NO TRIM!
        );

        console.log('✅ Decrypted filename:', fileName);

        // Decrypt metadata (if exists)
        let metadata = { notes: '', tags: [] };
        if (doc.encrypted_metadata) {
          try {
            const decryptedMeta = await decryptPatientData(
              doc.encrypted_metadata,
              doc.encryption_metadata,
              decryptionPassword.value  // ← NO TRIM!
            );
            metadata = typeof decryptedMeta === 'string' 
              ? JSON.parse(decryptedMeta) 
              : decryptedMeta;
            
            console.log('✅ Decrypted metadata:', metadata);
          } catch (e) {
            console.warn('⚠️ Failed to decrypt metadata for doc:', doc.id, e);
          }
        }

        decrypted.push({
          ...doc,
          decryptedName: fileName,
          decryptedMetadata: metadata
        });
      } catch (err) {
        console.error('❌ Failed to decrypt document:', doc.id);
        console.error('Error details:', err);
        
        decrypted.push({
          ...doc,
          decryptedName: `❌ Decryption Failed`,
          decryptedMetadata: { notes: '', tags: [] },
          decryptionError: true
        });
      }
    }

    decryptedDocs.value = decrypted;
    showPasswordModal.value = false;
    
    // Keep password in memory for uploads (don't clear it)
    return true;
  } catch (err) {
    console.error('💥 Overall decryption error:', err);
    passwordError.value = 'Failed to decrypt documents. Check your password.';
    return false;
  } finally {
    isDecrypting.value = false;
  }
};

// Filter documents by search query
const filteredDocuments = computed(() => {
  if (!decryptedDocs.value || decryptedDocs.value.length === 0) {
    return [];
  }

  if (!searchQuery.value || !searchQuery.value.trim()) {
    return decryptedDocs.value;
  }

  const query = searchQuery.value.toLowerCase();
  return decryptedDocs.value.filter(doc => {
    const nameMatch = doc.decryptedName?.toLowerCase().includes(query);
    const notesMatch = doc.decryptedMetadata?.notes?.toLowerCase().includes(query);
    const tagsMatch = doc.decryptedMetadata?.tags?.some(tag => 
      tag.toLowerCase().includes(query)
    );
    return nameMatch || notesMatch || tagsMatch;
  });
});

// Check if user can upload more documents
const canUpload = computed(() => {
  return documentService.canUploadDocument(slotsUsed.value, slotsAvailable.value);
});

// Slots percentage for progress bar
const slotsPercentage = computed(() => {
  if (slotsAvailable.value === 0) return 0;
  return (slotsUsed.value / slotsAvailable.value) * 100;
});

// Slots status color
const slotsStatus = computed(() => {
  const percentage = slotsPercentage.value;
  if (percentage >= 90) return 'error';
  if (percentage >= 70) return 'warning';
  return 'success';
});

// Load documents from API
const loadDocuments = async () => {
  try {
    isLoading.value = true;
    const response = await documentService.getMyDocuments(authToken.value);
    
    documents.value = response.documents || [];
    slotsUsed.value = response.slots_used || 0;
    slotsAvailable.value = response.slots_available || 1;

    // If we already have password, decrypt immediately
    if (decryptionPassword.value && documents.value.length > 0) {
      await decryptData();
    } else if (documents.value.length === 0) {
      // No documents, skip password prompt
      showPasswordModal.value = false;
    }
  } catch (err) {
    console.error('Failed to load documents:', err);
    message.error('Failed to load documents: ' + err.message);
    documents.value = [];
    showPasswordModal.value = false;
  } finally {
    isLoading.value = false;
  }
};

// Handle password submission
const submitPassword = async () => {
  const success = await decryptData();
  if (!success && documents.value.length > 0) {
    message.error('Invalid password or decryption failed');
  }
};

// Handle upload file selection
const handleFileSelect = (options) => {
  const { fileList } = options;
  if (fileList.length > 0) {
    uploadFile.value = fileList[0].file;
    
    // Validate file
    const validation = documentService.validateFile(uploadFile.value, 10);
    if (!validation.valid) {
      uploadError.value = validation.error;
      uploadFile.value = null;
      return;
    }
    
    // Pre-fill filename from file
    if (!uploadFileName.value) {
      uploadFileName.value = uploadFile.value.name;
    }
    
    uploadError.value = '';
  }
};

// Handle upload submission - USE SAME PASSWORD WITHOUT TRIM
const handleUpload = async () => {
  if (!uploadFile.value) {
    uploadError.value = 'Please select a file';
    return;
  }

  if (!uploadFileName.value.trim()) {
    uploadError.value = 'Please enter a filename';
    return;
  }

  if (!canUpload.value) {
    uploadError.value = 'Document limit reached. Upgrade to upload more.';
    return;
  }

  try {
    isUploading.value = true;
    uploadError.value = '';

    console.log('🔐 Encrypting with password (no trim)');

    // Use the SAME password variable - NO TRIM!
    const passwordToUse = decryptionPassword.value;

    // Encrypt filename
    const { encryptedData: encryptedFileName, encryptionMetadata: fileNameMeta } = 
      await encryptPatientData(uploadFileName.value.trim(), passwordToUse);

    console.log('✅ Encrypted filename:', encryptedFileName);
    console.log('📊 Encryption metadata:', fileNameMeta);

    // Encrypt metadata
    const metadata = {
      notes: uploadNotes.value.trim(),
      tags: [],
      uploadedAt: new Date().toISOString()
    };
    
    const { encryptedData: encryptedMetadata, encryptionMetadata: metadataMeta } = 
      await encryptPatientData(JSON.stringify(metadata), passwordToUse);

    // Use the same encryption metadata for both
    const encryptionMeta = fileNameMeta;

    console.log('📤 Uploading to server...');

    // Upload to server
    const result = await documentService.uploadDocument(
      authToken.value,
      uploadFile.value,
      encryptedFileName,
      encryptedMetadata,
      encryptionMeta
    );

    console.log('✅ Upload successful:', result);

    message.success('Document uploaded successfully!');
    
    // Reset upload form
    uploadFile.value = null;
    uploadFileName.value = '';
    uploadNotes.value = '';
    showUploadModal.value = false;

    // Reload documents
    await loadDocuments();
  } catch (err) {
    console.error('❌ Upload failed:', err);
    uploadError.value = err.message || 'Failed to upload document';
    message.error('Upload failed: ' + uploadError.value);
  } finally {
    isUploading.value = false;
  }
};

// Handle document download/view
const handleView = (doc) => {
  if (!doc.presigned_url) {
    message.error('No download URL available');
    return;
  }

  try {
    documentService.downloadDocument(doc.presigned_url, doc.decryptedName);
  } catch (err) {
    console.error('Download failed:', err);
    message.error('Failed to open document');
  }
};

// Handle delete confirmation
const confirmDelete = (doc) => {
  documentToDelete.value = doc;
  showDeleteModal.value = true;
};

// Handle delete submission
const handleDelete = async () => {
  if (!documentToDelete.value) return;

  try {
    isDeleting.value = true;
    await documentService.deleteDocument(authToken.value, documentToDelete.value.id);
    
    message.success('Document deleted successfully');
    showDeleteModal.value = false;
    documentToDelete.value = null;

    // Reload documents
    await loadDocuments();
  } catch (err) {
    console.error('Delete failed:', err);
    message.error('Failed to delete document: ' + err.message);
  } finally {
    isDeleting.value = false;
  }
};

// Cancel upload
const cancelUpload = () => {
  uploadFile.value = null;
  uploadFileName.value = '';
  uploadNotes.value = '';
  uploadError.value = '';
  showUploadModal.value = false;
};

// Initialize
onMounted(async () => {
  console.log('🔍 DOCUMENTS - Checking for stored password...');
  const storedPassword = sessionStorage.getItem('patientPassword');
  
  if (storedPassword) {
    console.log('✅ DOCUMENTS - Found stored password:', storedPassword);
    console.log('✅ DOCUMENTS - Password length:', storedPassword.length);
    console.log('✅ DOCUMENTS - Password bytes:', Array.from(storedPassword).map(c => c.charCodeAt(0)));
    decryptionPassword.value = storedPassword;
  } else {
    console.log('❌ DOCUMENTS - No stored password found');
  }

  await loadDocuments();
});
</script>

<template>
  <div class="documents-container">
    <!-- Password Prompt Modal - MATCH PatientProfile.vue -->
    <NModal
      v-model:show="showPasswordModal"
      preset="card"
      title="Enter Patient Password"
      :closable="false"
      :mask-closable="false"
      style="width: 500px"
    >
      <div class="password-prompt">
        <div class="prompt-icon">
          <NIcon size="48" color="#2080f0">
            <Lock />
          </NIcon>
        </div>

        <p class="prompt-text">
          Enter your patient password to decrypt and view your documents.
        </p>

        <NInput
          v-model:value="decryptionPassword"
          type="password"
          placeholder="Enter your patient password"
          size="large"
          :disabled="isDecrypting"
          @keyup.enter="submitPassword"
        >
          <template #prefix>
            <NIcon><Lock /></NIcon>
          </template>
        </NInput>

        <NAlert
          v-if="passwordError"
          type="error"
          :title="passwordError"
          style="margin-top: 16px"
        />
      </div>

      <template #footer>
        <NSpace justify="end">
          <NButton @click="router.push('/patient-profile')" :disabled="isDecrypting">
            Cancel
          </NButton>
          <NButton
            type="primary"
            @click="submitPassword"
            :loading="isDecrypting"
            :disabled="!decryptionPassword"
          >
            <template #icon>
              <NIcon><CheckCircle /></NIcon>
            </template>
            Decrypt Documents
          </NButton>
        </NSpace>
      </template>
    </NModal>

    <!-- Main Content -->
    <div v-if="!showPasswordModal" class="documents-content">
      <!-- Header -->
      <div class="page-header">
        <div class="header-content">
          <h1>My Documents</h1>
          <p class="subtitle">Securely store and manage your medical documents</p>
        </div>

        <NButton
          type="primary"
          size="large"
          @click="showUploadModal = true"
          :disabled="!canUpload"
        >
          <template #icon>
            <NIcon><Upload /></NIcon>
          </template>
          Upload Document
        </NButton>
      </div>

      <!-- Slots Indicator -->
      <NCard class="slots-card">
        <div class="slots-info">
          <div class="slots-text">
            <span class="slots-label">Storage Used:</span>
            <span class="slots-count">{{ slotsUsed }} / {{ slotsAvailable }} documents</span>
          </div>
          <NProgress
            type="line"
            :percentage="slotsPercentage"
            :status="slotsStatus"
            :height="8"
          />
        </div>
        
        <NAlert
          v-if="!canUpload"
          type="warning"
          title="Storage Full"
          style="margin-top: 16px"
        >
          You've reached your document limit. Upgrade to store more documents.
        </NAlert>
      </NCard>

      <!-- Search Bar -->
      <div v-if="documents.length > 0" class="search-bar">
        <NInput
          v-model:value="searchQuery"
          placeholder="Search documents by name, notes, or tags..."
          size="large"
          clearable
        >
          <template #prefix>
            <NIcon><Search /></NIcon>
          </template>
        </NInput>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <NSpin size="large" />
        <p>Loading documents...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="documents.length === 0" class="empty-state">
        <NEmpty description="No documents yet">
          <template #icon>
            <NIcon size="64" color="#ccc">
              <FolderOpen />
            </NIcon>
          </template>
          <template #extra>
            <NButton
              type="primary"
              @click="showUploadModal = true"
              :disabled="!canUpload"
            >
              <template #icon>
                <NIcon><Upload /></NIcon>
              </template>
              Upload Your First Document
            </NButton>
          </template>
        </NEmpty>
      </div>

      <!-- Documents Grid -->
      <div v-else-if="decryptedDocs.length > 0" class="documents-grid">
        <NGrid cols="1 s:2 m:3" :x-gap="16" :y-gap="16" responsive="screen">
          <NGridItem v-for="doc in filteredDocuments" :key="doc.id">
            <NCard class="document-card" hoverable>
              <div class="document-content">
                <!-- File Icon -->
                <div class="document-icon">
                  <NIcon size="40" :component="getFileIcon(doc.file_type, doc.decryptedName)" />
                </div>

                <!-- Document Info -->
                <div class="document-info">
                  <h3 class="document-name" :class="{ 'error-name': doc.decryptionError }">
                    {{ doc.decryptedName }}
                  </h3>
                  
                  <div class="document-meta">
                    <span class="meta-item">{{ formatSize(doc.file_size) }}</span>
                    <span class="meta-separator">•</span>
                    <span class="meta-item">{{ formatDate(doc.created_at) }}</span>
                  </div>

                  <!-- Notes -->
                  <p v-if="doc.decryptedMetadata?.notes" class="document-notes">
                    {{ doc.decryptedMetadata.notes }}
                  </p>

                  <!-- Free tag -->
                  <NTag v-if="doc.is_free" type="success" size="small" style="margin-top: 8px">
                    Free Tier
                  </NTag>
                </div>

                <!-- Actions -->
                <div class="document-actions">
                  <NTooltip trigger="hover">
                    <template #trigger>
                      <NButton
                        circle
                        @click="handleView(doc)"
                        :disabled="doc.decryptionError"
                      >
                        <template #icon>
                          <NIcon><Eye /></NIcon>
                        </template>
                      </NButton>
                    </template>
                    View Document
                  </NTooltip>

                  <NTooltip trigger="hover">
                    <template #trigger>
                      <NButton circle type="error" @click="confirmDelete(doc)">
                        <template #icon>
                          <NIcon><Trash2 /></NIcon>
                        </template>
                      </NButton>
                    </template>
                    Delete Document
                  </NTooltip>
                </div>
              </div>
            </NCard>
          </NGridItem>
        </NGrid>

        <!-- No search results -->
        <div v-if="filteredDocuments.length === 0 && searchQuery" class="no-results">
          <NEmpty description="No documents match your search">
            <template #icon>
              <NIcon size="48" color="#ccc">
                <Search />
              </NIcon>
            </template>
          </NEmpty>
        </div>
      </div>
    </div>

    <!-- Upload Modal -->
    <NModal
      v-model:show="showUploadModal"
      preset="card"
      title="Upload Document"
      style="width: 600px"
    >
      <div class="upload-modal">
        <NAlert type="info" title="Document Encryption" style="margin-bottom: 20px">
          Your document filename and metadata will be encrypted before upload.
        </NAlert>

        <!-- File Upload -->
        <div class="upload-section">
          <label class="upload-label">Select File *</label>
          <NUpload
            :max="1"
            :default-upload="false"
            @change="handleFileSelect"
            accept="*"
            list-type="text"
          >
            <NButton>
              <template #icon>
                <NIcon><Upload /></NIcon>
              </template>
              Choose File
            </NButton>
          </NUpload>
          <p class="upload-hint">Maximum file size: 10 MB</p>
        </div>

        <!-- Filename -->
        <div class="upload-section">
          <label class="upload-label">Document Name *</label>
          <NInput
            v-model:value="uploadFileName"
            placeholder="e.g., Blood Test Results - Jan 2024"
            size="large"
          />
        </div>

        <!-- Notes -->
        <div class="upload-section">
          <label class="upload-label">Notes (Optional)</label>
          <NInput
            v-model:value="uploadNotes"
            type="textarea"
            placeholder="Add any notes about this document..."
            :autosize="{ minRows: 3, maxRows: 6 }"
          />
        </div>

        <!-- Error Message -->
        <NAlert
          v-if="uploadError"
          type="error"
          :title="uploadError"
          closable
          @close="uploadError = ''"
          style="margin-top: 16px"
        />
      </div>

      <template #footer>
        <NSpace justify="end">
          <NButton @click="cancelUpload" :disabled="isUploading">
            Cancel
          </NButton>
          <NButton
            type="primary"
            @click="handleUpload"
            :loading="isUploading"
            :disabled="!uploadFile || !uploadFileName.trim()"
          >
            <template #icon>
              <NIcon><Upload /></NIcon>
            </template>
            Upload
          </NButton>
        </NSpace>
      </template>
    </NModal>

    <!-- Delete Confirmation Modal -->
    <NModal
      v-model:show="showDeleteModal"
      preset="card"
      title="Delete Document"
      style="width: 500px"
    >
      <div class="delete-modal">
        <div class="delete-icon">
          <NIcon size="48" color="#d03050">
            <AlertCircle />
          </NIcon>
        </div>

        <p class="delete-text">
          Are you sure you want to delete 
          <strong>{{ documentToDelete?.decryptedName }}</strong>?
        </p>

        <NAlert type="warning" title="Warning" style="margin-top: 16px">
          This action cannot be undone. The document will be permanently deleted.
        </NAlert>
      </div>

      <template #footer>
        <NSpace justify="end">
          <NButton @click="showDeleteModal = false" :disabled="isDeleting">
            Cancel
          </NButton>
          <NButton
            type="error"
            @click="handleDelete"
            :loading="isDeleting"
          >
            <template #icon>
              <NIcon><Trash2 /></NIcon>
            </template>
            Delete Document
          </NButton>
        </NSpace>
      </template>
    </NModal>
  </div>
</template>

<style scoped>
.documents-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.password-prompt {
  text-align: center;
  padding: 20px 0;
}

.prompt-icon {
  margin-bottom: 20px;
}

.prompt-text {
  margin-bottom: 24px;
  color: #666;
  font-size: 1rem;
}

.documents-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.header-content h1 {
  margin: 0;
  font-size: 2rem;
  color: #333;
  font-weight: 600;
}

.subtitle {
  margin: 4px 0 0 0;
  color: #666;
  font-size: 1rem;
}

.slots-card {
  border-radius: 12px;
}

.slots-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.slots-text {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.slots-label {
  font-weight: 500;
  color: #666;
}

.slots-count {
  font-weight: 600;
  color: #333;
  font-size: 1.1rem;
}

.search-bar {
  margin: 8px 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  gap: 16px;
}

.loading-state p {
  color: #666;
}

.empty-state {
  padding: 60px 20px;
}

.documents-grid {
  min-height: 200px;
}

.document-card {
  height: 100%;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.document-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.document-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.document-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  color: white;
}

.document-info {
  flex: 1;
}

.document-name {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  word-break: break-word;
}

.error-name {
  color: #d03050;
}

.document-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #999;
  font-size: 0.85rem;
}

.meta-separator {
  color: #ddd;
}

.document-notes {
  margin: 8px 0 0 0;
  color: #666;
  font-size: 0.9rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.document-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.no-results {
  padding: 40px 20px;
}

.upload-modal {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.upload-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.upload-label {
  font-weight: 500;
  color: #333;
}

.upload-hint {
  margin: 4px 0 0 0;
  color: #999;
  font-size: 0.85rem;
}

.delete-modal {
  text-align: center;
  padding: 20px 0;
}

.delete-icon {
  margin-bottom: 20px;
}

.delete-text {
  margin-bottom: 20px;
  color: #666;
  font-size: 1rem;
}

.delete-text strong {
  color: #333;
}

/* Responsive */
@media (max-width: 768px) {
  .documents-container {
    padding: 10px;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-content h1 {
    font-size: 1.6rem;
  }

  .document-card {
    margin-bottom: 12px;
  }
}
</style>